import { useState, useEffect, useCallback } from "react";
import {
  SimpleGrid,
  Card,
  Image,
  Text,
  Group,
  ActionIcon,
  Pagination,
  Center,
  Loader,
  Stack,
  Tooltip,
  Button,
  Textarea,
  Select,
  Checkbox,
  Badge,
  Modal,
} from "@mantine/core";
import {
  IconTrash,
  IconCopy,
  IconPlus as IconInsert,
  IconUpload,
  IconSparkles,
  IconCode,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  getContentAssets,
  deleteContentAsset,
  generateFreeFormImage,
} from "./contentApi";
import { useBrandContext } from "../Brands/brandContext";
import ContentAssetUploader from "./ContentAssetUploader";

export default function MediaGallery({ onInsert, embedded }) {
  const { selectedBrand } = useBrandContext();
  const [assets, setAssets] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState("");
  const [generateModel, setGenerateModel] = useState(
    "gemini-3.1-flash-image-preview",
  );
  const [generateAspectRatio, setGenerateAspectRatio] = useState("");
  const [generateContainsText, setGenerateContainsText] = useState(false);
  const [generateTransparent, setGenerateTransparent] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  const pageSize = 24;

  const fetchAssets = useCallback(() => {
    if (!selectedBrand?.id) return;
    getContentAssets(selectedBrand.id, activePage, pageSize).then(setAssets);
  }, [selectedBrand?.id, activePage]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await deleteContentAsset(id);
      fetchAssets();
      notifications.show({ message: "Asset deleted", color: "green" });
    } catch {
      notifications.show({ message: "Failed to delete asset", color: "red" });
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    notifications.show({ message: "URL copied to clipboard", color: "blue" });
  };

  const handleGenerate = async () => {
    if (!generatePrompt.trim()) return;
    setGenerating(true);
    try {
      const result = await generateFreeFormImage(
        selectedBrand.id,
        generatePrompt,
        generateModel,
        generateAspectRatio || null,
        generateContainsText,
        generateTransparent,
      );
      notifications.show({ message: "Image generated", color: "green" });
      setGeneratePrompt("");
      fetchAssets();
    } catch (err) {
      notifications.show({
        title: "Generation failed",
        message: err?.message || "Try a different prompt or model.",
        color: "red",
      });
    } finally {
      setGenerating(false);
    }
  };

  if (!selectedBrand) {
    return (
      <Center mt="xl">
        <Text>Please select a brand to view media</Text>
      </Center>
    );
  }

  return (
    <Stack mt="md">
      <Group justify="flex-end">
        <Button
          variant="light"
          leftSection={<IconSparkles size={16} />}
          onClick={() => {
            setShowGenerate(!showGenerate);
            setShowUpload(false);
          }}
        >
          Generate
        </Button>
        <Button
          variant="light"
          leftSection={<IconUpload size={16} />}
          onClick={() => {
            setShowUpload(!showUpload);
            setShowGenerate(false);
          }}
        >
          Upload
        </Button>
      </Group>

      {showGenerate && (
        <Card withBorder p="md">
          <Stack gap="sm">
            <Textarea
              placeholder="Describe the image you want to generate..."
              minRows={2}
              maxRows={5}
              autosize
              value={generatePrompt}
              onChange={(e) => setGeneratePrompt(e.currentTarget.value)}
            />
            <Group>
              <Select
                size="xs"
                w={180}
                value={generateModel}
                onChange={setGenerateModel}
                data={[
                  {
                    value: "gemini-3.1-flash-image-preview",
                    label: "Nano Banana 2",
                  },
                  {
                    value: "gpt-image-2",
                    label: "GPT Image 2.0",
                  },
                ]}
              />
              <Select
                size="xs"
                w={110}
                placeholder="Ratio"
                clearable
                value={generateAspectRatio}
                onChange={(v) => setGenerateAspectRatio(v || "")}
                data={[
                  { value: "1:1", label: "1:1" },
                  { value: "4:3", label: "4:3" },
                  { value: "3:4", label: "3:4" },
                  { value: "16:9", label: "16:9" },
                  { value: "9:16", label: "9:16" },
                  { value: "21:9", label: "21:9" },
                  { value: "3:2", label: "3:2" },
                  { value: "2:3", label: "2:3" },
                ]}
              />
              <Checkbox
                size="xs"
                label="Contains text"
                checked={generateContainsText}
                onChange={(e) =>
                  setGenerateContainsText(e.currentTarget.checked)
                }
              />
              <Checkbox
                size="xs"
                label="Transparent background"
                checked={generateTransparent}
                onChange={(e) =>
                  setGenerateTransparent(e.currentTarget.checked)
                }
              />
              <Button
                size="xs"
                leftSection={<IconSparkles size={14} />}
                onClick={handleGenerate}
                loading={generating}
                disabled={!generatePrompt.trim()}
              >
                Generate
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {showUpload && (
        <ContentAssetUploader
          message="Drop an image here to upload"
          onUploadComplete={() => {
            setShowUpload(false);
            fetchAssets();
          }}
        />
      )}

      {!assets ? (
        <Center mt="xl">
          <Loader size="xl" />
        </Center>
      ) : assets.data?.length === 0 ? (
        <Center mt="xl">
          <Text c="dimmed">No media assets yet</Text>
        </Center>
      ) : (
        <>
          <SimpleGrid
            // cols={{
            //   base: 2,
            //   sm: 3,
            //   md: embedded ? 3 : 4,
            //   lg: embedded ? 4 : 6,
            // }}
            cols={2}
          >
            {assets.data?.map((asset) => (
              <Card key={asset.id} withBorder padding="xs">
                <Card.Section
                  style={{ cursor: "pointer" }}
                  onClick={() => setPreviewAsset(asset)}
                >
                  <Image
                    src={asset.filePath}
                    h={140}
                    fit="cover"
                    fallbackSrc="https://placehold.co/300x200?text=Image"
                  />
                </Card.Section>
                <Text size="xs" mt="xs" lineClamp={1} title={asset.title}>
                  {asset.title || "Untitled"}
                </Text>
                <Group gap={4} mt={2}>
                  <Text size="xs" c="dimmed">
                    {new Date(asset.createdAt).toLocaleDateString()}
                  </Text>
                  {asset.containsText && (
                    <Badge size="xs" variant="light" color="yellow">
                      text
                    </Badge>
                  )}
                </Group>
                <Group mt="xs" gap={6}>
                  <Tooltip label="Copy URL">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      onClick={() => handleCopyUrl(asset.filePath)}
                    >
                      <IconCopy size={14} />
                    </ActionIcon>
                  </Tooltip>
                  {onInsert && (
                    <Tooltip label="Insert into content">
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="blue"
                        onClick={() =>
                          onInsert(asset.filePath, asset.title || "image")
                        }
                      >
                        <IconInsert size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Tooltip label="Delete">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color="red"
                      onClick={() => handleDelete(asset.id)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
          <Center>
            <Pagination
              total={Math.max(
                1,
                Math.ceil((assets.totalCount || 0) / pageSize),
              )}
              value={activePage}
              onChange={setActivePage}
              hideWithOnePage
            />
          </Center>
        </>
      )}

      <Modal
        opened={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        title={previewAsset?.title || "Image Preview"}
        size="xl"
        centered
      >
        {previewAsset && (
          <Stack gap="md">
            <Image
              src={previewAsset.filePath}
              fit="contain"
              fallbackSrc="https://placehold.co/800x600?text=Image"
            />
            {previewAsset.generationPrompt && (
              <Card withBorder p="xs">
                <Group gap={6} mb={4}>
                  <IconCode size={14} />
                  <Text size="xs" fw={600}>Generation Prompt</Text>
                </Group>
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                >
                  {previewAsset.generationPrompt}
                </Text>
              </Card>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
